import { useState } from "react";
import { Download, ShieldAlert, Trash2 } from "lucide-react";
import { ApiError, api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.js";

/** Account & privacy (M7, GDPR/CCPA): export or erase your data. */
export function AccountPage() {
  const { me, refresh } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirmDelete() {
    setError(null);
    setDeleting(true);
    try {
      await api.deleteAccount();
      // Session is gone server-side; refresh() clears `me` → redirect to login.
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Deletion failed");
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Account & privacy</h1>
      <p className="text-sm text-muted-foreground">Signed in as {me?.user.email}</p>

      <Card>
        <CardHeader>
          <CardTitle>Export your data</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Download everything we hold about you — profile, credentials, resumes, skills, and CVs — as JSON.
          </p>
          <Button asChild variant="secondary">
            <a href={api.exportAccountUrl()} download>
              <Download size={16} /> Download my data
            </a>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">Delete your account</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Permanently deletes your resumes, skills, and CVs, and removes your name from credentials issued to
            you. Issued credentials themselves are retained by their issuers for records and tamper-evidence, but
            are no longer linked to you. This cannot be undone.
          </p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 size={16} /> Delete my account
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert size={18} className="text-destructive" /> Delete account?
              </DialogTitle>
              <DialogDescription className="mt-2">
                This permanently erases your data and signs you out. This action can&apos;t be undone.
              </DialogDescription>
              <div className="mt-5 flex justify-end gap-2">
                <DialogClose asChild>
                  <Button variant="secondary" size="sm">
                    Cancel
                  </Button>
                </DialogClose>
                <Button variant="destructive" size="sm" onClick={() => void confirmDelete()} disabled={deleting}>
                  {deleting ? "Deleting…" : "Yes, delete everything"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </div>
  );
}
